import { useState } from "react";
import { Accordion } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { FiHelpCircle, FiPlus, FiSearch } from "react-icons/fi";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import "../about/about.css";
import "./faq.css";
import { faqAr, faqEn, FaqTable } from "./faqContent";

const HELP_CENTER = "https://intercom.help/tygo/ar";
const ALL = -1;

const Faq = () => {
    const location = useLocation();
    const isHeadless = new URLSearchParams(location.search).get("headless") === "true";

    return (
        <div>
            <div className="container about mb-5">
                {!isHeadless && <Header />}
                <FaqInner />
            </div>
            {!isHeadless && <Footer />}
        </div>
    );
};

export const FaqInner = () => {
    const { i18n } = useTranslation();
    const content = i18n.language === "ar" ? faqAr : faqEn;
    const [query, setQuery] = useState("");
    const [active, setActive] = useState<number>(ALL);

    const term = query.trim().toLowerCase();
    const sections = content.sections
        .map((section, index) => ({
            ...section,
            index,
            items: section.items.filter((item) => !term || item.q.toLowerCase().includes(term)),
        }))
        .filter((section) => (active === ALL || section.index === active) && section.items.length > 0);

    // the contacts table isn't a question, so hide it while searching or filtering
    const showContacts = !term && active === ALL;

    return (
        <div className="faq-page">
            <div className="faq-hero">
                <span className="faq-hero-icon"><FiHelpCircle size={26} /></span>
                <h1 className="faq-title">{content.title}</h1>
                <p className="faq-subtitle">{content.subtitle}</p>

                <label className="faq-search">
                    <FiSearch size={18} />
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={content.ui.search}
                        aria-label={content.ui.search}
                    />
                </label>
            </div>

            <div className="faq-chips" role="tablist">
                {[{ title: content.ui.all, index: ALL }, ...content.sections.map((s, index) => ({ title: s.title, index }))].map((chip) => (
                    <button
                        key={chip.index}
                        type="button"
                        role="tab"
                        aria-selected={active === chip.index}
                        className={`faq-chip ${active === chip.index ? "active" : ""}`}
                        onClick={() => setActive(chip.index)}
                    >
                        {chip.title}
                    </button>
                ))}
            </div>

            {sections.length === 0 && <p className="faq-empty">{content.ui.noResults}</p>}

            {sections.map((section) => (
                <section key={section.title} className="faq-section">
                    <h2 className="faq-section-title">
                        {section.title}
                        <span className="faq-count">{section.items.length}</span>
                    </h2>
                    <Accordion alwaysOpen className="faq-card faq-accordion">
                        {section.items.map((item) => (
                            <Accordion.Item key={item.q} eventKey={`${section.index}-${item.q}`}>
                                <Accordion.Header as="h3">
                                    <span className="faq-question">{item.q}</span>
                                    <span className="faq-toggle" aria-hidden="true"><FiPlus size={18} /></span>
                                </Accordion.Header>
                                <Accordion.Body>{item.a}</Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </section>
            ))}

            {showContacts && (
                <section className="faq-section">
                    <h2 className="faq-section-title">{content.contacts.title}</h2>
                    <div className="faq-card faq-contacts">
                        <FaqTable headers={content.contacts.headers} rows={content.contacts.rows} phones />
                    </div>
                </section>
            )}

            <div className="faq-help">
                <div>
                    <h2 className="faq-help-title">{content.ui.helpTitle}</h2>
                    <p className="faq-help-desc">{content.ui.helpDesc}</p>
                </div>
                <a className="faq-help-cta" href={HELP_CENTER} target="_blank" rel="noreferrer">
                    {content.ui.helpCta}
                </a>
            </div>
        </div>
    );
};

export default Faq;
